import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import apiClient, { getErrorMessage } from '../api/client';

export const AuthContext = createContext(null);

function persistSession({ user, tokens, organisationId }) {
  localStorage.setItem('user', JSON.stringify(user));
  localStorage.setItem('accessToken', tokens.accessToken);
  localStorage.setItem('refreshToken', tokens.refreshToken);
  if (organisationId) localStorage.setItem('organisationId', organisationId);
}

function clearSession() {
  localStorage.removeItem('user');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('organisationId');
  localStorage.removeItem('organisations');
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organisations, setOrganisations] = useState([]);
  const [organisationId, setOrganisationId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('accessToken');
    const storedOrgs = localStorage.getItem('organisations');
    const storedOrgId = localStorage.getItem('organisationId');

    if (storedUser && token) {
      try {
        setUser(JSON.parse(storedUser));
        setOrganisations(storedOrgs ? JSON.parse(storedOrgs) : []);
        setOrganisationId(storedOrgId || null);
        setIsAuthenticated(true);
      } catch {
        clearSession();
      }
    }
    setIsLoading(false);

    const onSessionExpired = () => {
      clearSession();
      setUser(null);
      setIsAuthenticated(false);
    };
    window.addEventListener('auth:session-expired', onSessionExpired);
    return () => window.removeEventListener('auth:session-expired', onSessionExpired);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  /**
   * Step 1 of 2. Does NOT persist a session or authenticate the user — every sign-in
   * requires OTP verification (mandatory 2FA). Returns the challenge for VerifyOtp.js
   * to consume; call verifySigninOtp() with the code to actually complete sign-in.
   */
  const signin = useCallback(async (email, password) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/auth/login', { email, password });
      return response.data;
    } catch (err) {
      const message = getErrorMessage(err, 'Sign in failed');
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Step 1 of 2. Creates the account but does NOT persist a session — the email must be
   * verified via OTP first. Call verifySignupOtp() with the code to complete signup and
   * auto sign-in.
   */
  const signup = useCallback(async (formData) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/auth/register', {
        organisationLegalName: formData.companyName,
        businessType: formData.businessType || 'ltd',
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
      });
      return response.data;
    } catch (err) {
      const message = getErrorMessage(err, 'Sign up failed');
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /** Step 2 of signup — verifies the OTP, then auto signs the user in. */
  const verifySignupOtp = useCallback(async (challengeToken, code) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/auth/verify-signup-otp', { challengeToken, code });
      const { user: userData, organisation, tokens } = response.data.data;

      persistSession({ user: userData, tokens, organisationId: organisation.id });
      const orgs = [{
        organisationId: organisation.id,
        legalName: organisation.legalName,
        tradingName: null,
        status: 'active',
        isPrimaryContact: true,
      }];
      localStorage.setItem('organisations', JSON.stringify(orgs));

      setUser(userData);
      setOrganisations(orgs);
      setOrganisationId(organisation.id);
      setIsAuthenticated(true);

      return response.data;
    } catch (err) {
      const message = getErrorMessage(err, 'Verification failed');
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /** Step 2 of signin — verifies the OTP, then completes sign-in. */
  const verifySigninOtp = useCallback(async (challengeToken, code) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/auth/verify-signin-otp', { challengeToken, code });
      const { user: userData, tokens, organisations: orgs } = response.data.data;

      const defaultOrg = orgs.find((o) => o.isPrimaryContact) || orgs[0] || null;
      persistSession({ user: userData, tokens, organisationId: defaultOrg?.organisationId });
      localStorage.setItem('organisations', JSON.stringify(orgs));

      setUser(userData);
      setOrganisations(orgs);
      setOrganisationId(defaultOrg?.organisationId || null);
      setIsAuthenticated(true);

      return response.data;
    } catch (err) {
      const message = getErrorMessage(err, 'Verification failed');
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /** Issues a fresh code + a fresh challenge token (the old one may be near expiry). */
  const resendOtp = useCallback(async (challengeToken) => {
    setError(null);
    try {
      const response = await apiClient.post('/auth/resend-otp', { challengeToken });
      return response.data;
    } catch (err) {
      const message = getErrorMessage(err, 'Could not resend code');
      setError(message);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    try {
      if (refreshToken) await apiClient.post('/auth/logout', { refreshToken });
    } catch {
      // Best-effort — clear local session regardless of whether the server call succeeded.
    }
    clearSession();
    setUser(null);
    setOrganisations([]);
    setOrganisationId(null);
    setIsAuthenticated(false);
    setError(null);
  }, []);

  const switchOrganisation = useCallback((newOrgId) => {
    localStorage.setItem('organisationId', newOrgId);
    setOrganisationId(newOrgId);
  }, []);

  const resetPassword = useCallback(async (email) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/auth/forgot-password', { email });
      return response.data;
    } catch (err) {
      const message = getErrorMessage(err, 'Password reset failed');
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

    /** Consumes the token from the emailed reset link and sets a new password. */
  const confirmResetPassword = useCallback(async (token, newPassword) => {
    setIsLoading(true);
    setError(null);
    try {
      await apiClient.post('/auth/reset-password', { token, newPassword });
    } catch (err) {
      const message = getErrorMessage(err, 'Could not reset password');
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value = {
    user,
    organisations,
    organisationId,
    isAuthenticated,
    isLoading,
    error,
    signin,
    signup,
    verifySignupOtp,
    verifySigninOtp,
    resendOtp,
    logout,
    switchOrganisation,
    resetPassword,
    confirmResetPassword,
    setError,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export default AuthContext;